import React, { useState } from 'react';
import { X, Save, Loader2, MapPin } from 'lucide-react';
import GoogleMapPicker from '../GoogleMapPicker';
import PlacesAutocomplete from '../PlacesAutocomplete';
import { safeFetch, extractError } from '../../lib/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SetSupplierLocationModal — lets staff (or admin) pin a supplier's location.
 *
 * Shows a Google Map (regular + satellite toggle, sharp pin) plus a Places
 * search input. On save, calls `PUT /api/suppliers/{id}` with the new lat/lng.
 *
 * Props:
 *   supplier: supplier object (must include id, name, location)
 *   onClose()
 *   onSaved(updatedSupplier) — called with {latitude, longitude} merged in
 */
export default function SetSupplierLocationModal({ supplier, onClose, onSaved }) {
  const existing = supplier?.location && (supplier.location.latitude || supplier.location.longitude)
    ? { latitude: supplier.location.latitude, longitude: supplier.location.longitude }
    : null;

  const [pin, setPin] = useState(existing);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const useMyGps = () => {
    if (!navigator.geolocation) { alert('GPS not available on this device.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setPin({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => alert('Could not read GPS. Please enable location.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (!pin) { setErr('Drop a pin or search a location first.'); return; }
    setBusy(true); setErr('');
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/suppliers/${supplier.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ latitude: pin.latitude, longitude: pin.longitude }),
    });
    setBusy(false);
    if (result.ok) {
      onSaved?.({ ...supplier, location: { latitude: pin.latitude, longitude: pin.longitude }, has_location: true });
      onClose?.();
    } else {
      setErr(extractError(result, 'Save failed'));
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="set-location-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="flex items-start justify-between p-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <MapPin size={18} className="text-red-600" /> Set location for
            </h3>
            <p className="text-sm text-gray-700 mt-0.5 font-semibold">{supplier?.name}</p>
            <p className="text-[11px] text-gray-500">
              Pick on the map, search, or use your current GPS. The first person to save a valid location
              becomes the location owner — only they or an admin can edit it later.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid="set-location-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Search (Google Places)</label>
            <PlacesAutocomplete
              testId="set-location-places"
              placeholder="Search address, shop, landmark…"
              onPlacePicked={(p) => setPin({ latitude: p.latitude, longitude: p.longitude })}
            />
          </div>

          <GoogleMapPicker
            value={pin}
            onChange={(c) => setPin({ latitude: c.latitude, longitude: c.longitude })}
            height={320}
          />

          <div className="flex items-center gap-2 text-[11px] text-gray-600">
            <button
              type="button"
              onClick={useMyGps}
              className="px-2.5 py-1 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="set-location-gps-btn"
            >
              Use my current GPS
            </button>
            <span className="text-gray-400">·</span>
            <span className="font-mono">
              Lat: {pin?.latitude ? Number(pin.latitude).toFixed(6) : '—'} · Lng: {pin?.longitude ? Number(pin.longitude).toFixed(6) : '—'}
            </span>
          </div>

          {err && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="set-location-err">
              {err}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={save}
            disabled={busy || !pin}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white flex items-center gap-1 disabled:opacity-60"
            data-testid="set-location-save-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Location
          </button>
        </div>
      </div>
    </div>
  );
}
